/**
 * Document Processing Utilities
 * Extract text from PDF, DOCX, and TXT files
 */

import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";

// Set worker path for PDF.js - Use dynamic version to match the library
if (typeof window !== "undefined") {
  // We use unpkg as a reliable CDN for the worker file
  const version = pdfjsLib.version;
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
}

export interface DocumentValidation {
  isValid: boolean;
  error?: string;
  fileType?: string;
  fileSize?: number;
}

export interface ExtractedContent {
  text: string;
  pageCount?: number;
  wordCount: number;
  metadata?: Record<string, any>;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

/**
 * Validate document file before processing
 */
export function validateDocumentFile(file: File): DocumentValidation {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `El archivo es demasiado grande. Máximo permitido: ${MAX_FILE_SIZE / 1024 / 1024}MB`,
      fileSize: file.size,
    };
  }

  // Check file type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: "Tipo de archivo no soportado. Solo se permiten PDF, DOCX y TXT",
      fileType: file.type,
    };
  }

  // Check if file is empty
  if (file.size === 0) {
    return {
      isValid: false,
      error: "El archivo está vacío",
      fileSize: 0,
    };
  }

  return {
    isValid: true,
    fileType: file.type,
    fileSize: file.size,
  };
}

/**
 * Extract text from PDF file
 */
export async function extractTextFromPDF(
  file: File
): Promise<ExtractedContent> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    const numPages = pdf.numPages;
    let fullText = "";

    // Extract text from each page
    for (let i = 1; i <= numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(" ");
      fullText += pageText + "\n\n";
    }

    const wordCount = countWords(fullText);

    return {
      text: fullText.trim(),
      pageCount: numPages,
      wordCount,
      metadata: {
        extractedAt: new Date().toISOString(),
        source: "pdf",
      },
    };
  } catch (error: any) {
    console.error("Error extracting text from PDF:", error);
    throw new Error(
      `Error al extraer texto del PDF: ${error.message || "Error desconocido"}`
    );
  }
}

/**
 * Extract text from DOCX file
 */
export async function extractTextFromDOCX(
  file: File
): Promise<ExtractedContent> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });

    const text = result.value;
    const wordCount = countWords(text);

    // Check if there were any warnings
    if (result.messages.length > 0) {
      console.warn("DOCX extraction warnings:", result.messages);
    }

    return {
      text: text.trim(),
      wordCount,
      metadata: {
        extractedAt: new Date().toISOString(),
        source: "docx",
        warnings: result.messages.length > 0 ? result.messages : undefined,
      },
    };
  } catch (error: any) {
    console.error("Error extracting text from DOCX:", error);
    throw new Error(
      `Error al extraer texto del DOCX: ${error.message || "Error desconocido"}`
    );
  }
}

/**
 * Extract text from TXT file
 */
export async function extractTextFromTXT(
  file: File
): Promise<ExtractedContent> {
  try {
    const text = await file.text();
    const wordCount = countWords(text);

    return {
      text: text.trim(),
      wordCount,
      metadata: {
        extractedAt: new Date().toISOString(),
        source: "txt",
      },
    };
  } catch (error: any) {
    console.error("Error extracting text from TXT:", error);
    throw new Error(
      `Error al leer el archivo de texto: ${error.message || "Error desconocido"}`
    );
  }
}

/**
 * Extract text from any supported document type
 */
export async function extractTextFromDocument(
  file: File
): Promise<ExtractedContent> {
  // Validate file first
  const validation = validateDocumentFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error || "Archivo inválido");
  }

  // Determine file type and extract accordingly
  const mimeType = file.type;

  switch (mimeType) {
    case "application/pdf":
      return await extractTextFromPDF(file);

    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return await extractTextFromDOCX(file);

    case "text/plain":
      return await extractTextFromTXT(file);

    default:
      throw new Error(`Tipo de archivo no soportado: ${mimeType}`);
  }
}

/**
 * Count words in text
 */
function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

/**
 * Get file type display name
 */
export function getFileTypeDisplayName(mimeType: string): string {
  const typeMap: Record<string, string> = {
    "application/pdf": "PDF",
    "application/msword": "Word (DOC)",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word (DOCX)",
    "text/plain": "Texto",
  };

  return typeMap[mimeType] || "Desconocido";
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

/**
 * Check if document is too long for analysis
 */
export function isDocumentTooLong(content: ExtractedContent): boolean {
  const MAX_WORDS = 10000; // Adjust based on token limits
  return content.wordCount > MAX_WORDS;
}

/**
 * Truncate document if too long
 */
export function truncateDocument(
  content: ExtractedContent,
  maxWords: number = 10000
): ExtractedContent {
  if (content.wordCount <= maxWords) {
    return content;
  }

  const words = content.text.split(/\s+/);
  const truncatedText = words.slice(0, maxWords).join(" ");

  return {
    ...content,
    text: truncatedText + "\n\n[... documento truncado ...]",
    wordCount: maxWords,
    metadata: {
      ...content.metadata,
      truncated: true,
      originalWordCount: content.wordCount,
    },
  };
}
